"use client"
import { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { EmailIcon, PhoneIcon, AddIcon, ViewIcon, CheckIcon } from "@chakra-ui/icons"
import {
  Card,
  CardBody,
  Flex,
  Box,
  Text,
  Divider,
  AlertIcon,
  Alert,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Input,
  Tooltip,
  ModalFooter,
  Image,
  useToast,
} from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser } from "@fortawesome/free-solid-svg-icons"
import { useNavigate } from "react-router-dom"
import { FaBriefcase } from "react-icons/fa"


import {
  fetchCitiesAsync,
  fetchCountryAsync,
  fetchStateAsync,
  selectFetchCities,
  selectFetchCountry,
  selectFetchState,
} from "./AboutSlice"
import { CANDIDATE_PROFILE, RESUME_VIEW } from "../../../common/URLs"
import { MonthPicker, YearPicker } from "../../../common/YearPicker"
import { fetchBasicDetailsBySingleParamAsync, selectBasicDetailsBySingleParam, addCandidateProfileAsync, addCandidateResumeAsync, updateCandidateDetailsAsync } from "../../../profile/BasicDetailsSingleParam/BasicDetailsSingleParamSlice"

function CandidateProfile() {
  const dispatch = useDispatch()
  const basicDetails = useSelector(selectBasicDetailsBySingleParam)
  const navigate = useNavigate()
  const countryList = useSelector(selectFetchCountry)
  const stateList = useSelector(selectFetchState)
  const citiesList = useSelector(selectFetchCities)

  // Track initial fetches with useRef
  const hasFetchedBasicDetails = useRef(false)
  const hasFetchedCountries = useRef(false)
  const hasFetchedStates = useRef(false)
  const hasFetchedCities = useRef(false)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm()

  const [selectedCountryId, setSelectedCountryId] = useState("")
  const [selectedStateId, setSelectedStateId] = useState("")

  const countryChange = (e) => {
    const countryId = e.target.value
    setSelectedCountryId(countryId)
  }

  const stateChange = (e) => {
    const stateId = e.target.value
    setSelectedStateId(stateId)
  }

  // Fetch states when selectedCountryId changes
  // Fetch basic details and country list on component mount
  useEffect(() => {
    if (!hasFetchedBasicDetails.current && !basicDetails) {
      hasFetchedBasicDetails.current = true
      dispatch(fetchBasicDetailsBySingleParamAsync())
    }

    if (!hasFetchedCountries.current && countryList.length === 0) {
      hasFetchedCountries.current = true
      dispatch(fetchCountryAsync())
    }
  }, [dispatch, basicDetails, countryList.length])

  // Fetch states when selectedCountryId changes
  useEffect(() => {
    if (selectedCountryId && !hasFetchedStates.current) {
      hasFetchedStates.current = true
      dispatch(fetchStateAsync(selectedCountryId))
    }
  }, [selectedCountryId, dispatch])

  // Fetch cities when selectedStateId changes
  useEffect(() => {
    if (selectedStateId && !hasFetchedCities.current) {
      hasFetchedCities.current = true
      dispatch(fetchCitiesAsync(selectedStateId))
    }
  }, [selectedStateId, dispatch])

  const { isOpen: isOpen1, onOpen: onOpen1, onClose: onClose1 } = useDisclosure()
  const { isOpen: isOpen2, onOpen: onOpen2, onClose: onClose2 } = useDisclosure()
  const { isOpen: isOpen3, onOpen: onOpen3, onClose: onClose3 } = useDisclosure()

  // Fetch basic details and country list on component mount


  const toast = useToast()
  const [isLoading, setIsLoading] = useState(true)

  const handleUpdate = async (candidateData) => {
    // Comprehensive validation
    const validationErrors = []
    // Validate required fields
    const requiredFields = [
      "address",
      "city_uuid",
      "state_uuid",
      "country_uuid",
      "dob",
      "gender",
      "marital_status",
      "pincode",
    ]
    requiredFields.forEach((field) => {
      if (!candidateData[field]) {
        validationErrors.push(`${field.replace("_", " ").toUpperCase()} is required`)
      }
    })
    // Date of Birth validation
    const dob = new Date(candidateData.dob)
    const currentDate = new Date()
    const minAge = new Date(currentDate.getFullYear() - 18, currentDate.getMonth(), currentDate.getDate())
    const maxAge = new Date(currentDate.getFullYear() - 80, currentDate.getMonth(), currentDate.getDate())
    if (dob > currentDate) {
      validationErrors.push("Date of Birth cannot be in the future")
    }
    if (dob > minAge) {
      validationErrors.push("You must be at least 18 years old")
    }
    if (dob < maxAge) {
      validationErrors.push("Age cannot be more than 80 years")
    }
    // Experience validation
    const experienceYears = Number.parseInt(candidateData.experience_year || 0)
    const experienceMonths = Number.parseInt(candidateData.experience_month || 0)
    if (experienceYears > 50) {
      validationErrors.push("Experience years cannot exceed 50 years")
    }
    if (experienceMonths > 11) {
      validationErrors.push("Experience months cannot exceed 11 months")
    }
    if (experienceYears === 50 && experienceMonths > 0) {
      validationErrors.push("Maximum experience is 50 years")
    }
    // Pincode validation
    const pincode = candidateData.pincode
    if (pincode && (pincode.toString().length !== 6 || !/^\d{6}$/.test(pincode))) {
      validationErrors.push("Pincode must be a 6-digit number")
    }
    // Profile summary length validation
    if (candidateData.profile_summery && candidateData.profile_summery.length > 1000) {
      validationErrors.push("Profile summary cannot exceed 1000 characters")
    }
    // If validation errors exist, show toast and prevent submission
    if (validationErrors.length > 0) {
      toast({
        title: "Validation Errors",
        description: validationErrors.map((error, index) => `${index + 1}. ${error}`).join("\n"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      })
      return
    }
    // Prepare payload
    const payload = {
      active_job_search: candidateData.active_job_search === "true",
      address: candidateData.address || "",
      address1: candidateData.address1 || "",
      city_uuid: candidateData.city_uuid,
      state_uuid: candidateData.state_uuid,
      country_uuid: candidateData.country_uuid,
      dob: candidateData.dob,
      experience_month: candidateData.experience_month,
      experience_year: candidateData.experience_year,
      gender: candidateData.gender,
      marital_status: candidateData.marital_status,
      pincode: candidateData.pincode,
      resume: candidateData.resume,
      profile_pic: candidateData.profile_pic,
      profile_summery: candidateData.profile_summery,
      work_status: candidateData.work_status,
    }
    try {
      // Start loading state
      setIsLoading(true)
      // Dispatch update action
      await dispatch(
        updateCandidateDetailsAsync({
          uuid: candidateData.uuid,
          candidateData: payload,
        }),
      )
      // Refresh basic details
      await dispatch(fetchBasicDetailsBySingleParamAsync())
      // Show success toast
      toast({
        title: "Update Successful",
        description: "Candidate details have been updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      })
      // Reset form and close modal
      reset()
      onClose1()
    } catch (error) {
      // Show error toast
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update candidate details",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      })
      console.error("Failed to update candidate details", error)
    } finally {
      // End loading state
      setIsLoading(false)
    }
  }

  const openUpdateModal = (basicDetails) => {
    const data = basicDetails.basic_details
    // Set initial country and state selection using the correct uuid
    // These state updates will trigger the useEffects to fetch state and city lists
    setSelectedCountryId(data.country?.uuid || "")
    setSelectedStateId(data.state?.uuid || "")

    // Set form values
    Object.keys(data).forEach((key) => {
      // Special handling for country, state, and city to use their UUIDs
      if (key === "country") {
        setValue("country_uuid", data.country?.uuid || "")
      } else if (key === "state") {
        setValue("state_uuid", data.state?.uuid || "")
      } else if (key === "city") {
        setValue("city_uuid", data.city?.uuid || "")
      } else {
        setValue(key, data[key])
      }
    })
    setValue("marital_status", data.marital_status || "")
    console.log("status", data)

    // Open the modal. The data for dropdowns will be populated asynchronously by useEffects.
    onOpen1()
  }

  const [selectedProfileFile, setSelectedProfileProfileFile] = useState(null)
  const [previewProfileUrl, setPreviewProfileUrl] = useState(null)
  const [selectedResumeFile, setSelectedResumeFile] = useState(null)
  const [previewResumeUrl, setPreviewResumeUrl] = useState(null)

  // Handle file selection
  const handleProfileFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setSelectedProfileProfileFile(file)
      setPreviewProfileUrl(URL.createObjectURL(file))
    }
  }

  // ============ MODIFIED CODE START ============
  const handleResumeFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword", // for .doc
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // for .docx
      ]

      if (allowedTypes.includes(file.type)) {
        setSelectedResumeFile(file)
        setPreviewResumeUrl(URL.createObjectURL(file))
      } else {
        toast({
          title: "Invalid File Type",
          description: "Please upload a valid PDF, DOC, or DOCX file.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        })
        // Reset the input and state if an invalid file is selected
        e.target.value = null
        setSelectedResumeFile(null)
        setPreviewResumeUrl(null)
      }
    }
  }
  // ============ MODIFIED CODE END ============

  const saveResume = async () => {
    if (!selectedResumeFile) {
      toast({
        title: "Resume Upload Error",
        description: "The resume field is required.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      })
      return // Prevent further execution
    }
    const formData = new FormData()
    formData.append("resume", selectedResumeFile)
    try {
      const response = await dispatch(addCandidateResumeAsync({ formData }))
      await dispatch(fetchBasicDetailsBySingleParamAsync())
      onClose3()
      if (response?.payload?.status === "success") {
        toast({
          title: "Resume Upload Successful",
          description: "Your resume has been uploaded successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        })
      } else {
        toast({
          title: "Resume Upload Failed",
          description: "Unable to upload resume. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        })
      }
    } catch (error) {
      toast({
        title: "Resume Upload Error",
        description: "An unexpected error occurred while uploading resume.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      })
    }
  }

  const saveProfile = async () => {
    if (!selectedProfileFile) {
      toast({
        title: "Profile Picture Upload Error",
        description: "The profile picture field is required.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      })
      return // Prevent further execution
    }
    const formData = new FormData()
    formData.append("profile_pic", selectedProfileFile)
    try {
      const response = await dispatch(addCandidateProfileAsync({ formData }))
      await dispatch(fetchBasicDetailsBySingleParamAsync())
      onClose2()
      if (response?.payload?.status === "success") {
        toast({
          title: "Profile Picture Upload Successful",
          description: "Your profile picture has been uploaded successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        })
      } else {
        toast({
          title: "Profile Picture Upload Failed",
          description: "Unable to upload profile picture. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        })
      }
    } catch (error) {
      toast({
        title: "Profile Picture Upload Error",
        description: "An unexpected error occurred while uploading profile picture.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      })
    }
  }

  const openPdf = (url) => {
    if (!url) {
      console.error("File URL is missing.")
      return
    }
    const newTab = window.open(url, "_blank")
    if (!newTab) {
      console.error("Failed to open file. Please check your browser settings.")
    }
  }

  useEffect(() => {
    if (basicDetails && Object.keys(basicDetails).length > 0) {
      if (basicDetails.basic_details === undefined || basicDetails.basic_details === null) {
        navigate("/candidate-profile")
      }
    }
  }, [basicDetails, navigate])

  useEffect(() => {
    if (basicDetails?.basic_details?.active_job_search !== undefined) {
      setValue("active_job_search", basicDetails.basic_details.active_job_search === 1)
    }
  }, [basicDetails, setValue])

  const handleVerifyEmail = () => {
    navigate("/candidate-dashboard/verify-email")
  }

  const handleVerifyPhone = () => {
    navigate("/phone-verification-phone")
  }

  // Helper function to calculate the SVG strokeDasharray for the progress circle
  const calculateProgress = (percentage) => {
    const radius = 50
    const circumference = 2 * Math.PI * radius
    const progress = (percentage / 100) * circumference
    return `${progress}, ${circumference}`
  }

  return (
    <div style={{ fontFamily: "Inter, sans-serif" }}>
      <Card
        // minW="6xl"
        w="full"
        boxShadow=""
        borderRadius="lg"
        overflow="hidden"
        className="shadow-sm"
      >
        <CardBody>
          {basicDetails ? (
            <>
              <Flex
                alignItems="center"
                justify={"space-between"}
                mb={4}
                flexDirection={{ base: "column", md: "row" }}
                position="relative"

              >
                {/* Profile Image with Circular Progress */}
                <div className="relative inline-block">
                  <div className="relative w-20 h-20">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      {/* Background circle (gray) */}
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#e2e8f0" strokeWidth="5" />
                      {/* Progress circle (blue) - dynamically calculated */}
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="#3182ce"
                        strokeWidth="5"
                        strokeLinecap="round"
                        strokeDasharray={calculateProgress(basicDetails.profileCompletion || 0)}
                        strokeDashoffset="25"
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    {/* Profile Image or Icon */}
                    <div
                      className="absolute inset-0 flex items-center justify-center rounded-full overflow-hidden"
                      style={{
                        top: "10%",
                        left: "10%",
                        right: "10%",
                        bottom: "10%",
                      }}
                    >
                      {basicDetails?.basic_details?.profile_pic ? (
                        <Image
                          name={basicDetails.name}
                          src={`${CANDIDATE_PROFILE}${basicDetails?.basic_details?.profile_pic}`}
                          className="h-full w-full object-cover rounded-full"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={faUser}
                          className="h-full w-full flex items-center justify-center rounded-full bg-gray-200 text-gray-500"
                        />
                      )}
                    </div>
                    {/* Percentage Text */}
                    <div className="absolute -bottom-2 left-0 right-0 text-center">
                      <span className="text-xs font-bold text-blue-600 bg-white px-1 rounded">
                        {basicDetails.profileCompletion || 0}%
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={onOpen2}
                    className="absolute top-0 right-0 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-blue-600"
                  >
                    +
                  </button>
                </div>
                <Box
                  marginLeft={{ base: "0", md: "1rem" }}
                  width="full"
                  height="full"
                  textAlign={{ base: "center", md: "left" }}
                >
                  <div className="text-2xl font-semibold capitalize">{basicDetails.name}</div>
                  <div className="">

                    <h6 className="text-gray-500  text-sm mb-2 ">
                      {basicDetails.email}
                      {basicDetails?.email_verify === 1 ? (
                        <CheckIcon color="green.500" className="ml-2" />
                      ) : (
                        <></>
                      )}
                    </h6>

                  </div>

                </Box>
                <Button
                  onClick={() => openUpdateModal(basicDetails)}
                  position="absolute"
                  top={1}
                  right={1}
                  colorScheme="blue"
                  className="flex items-center justify-center space-x-1s lg:space-x-2 px-2s lg:px-4 lg:py-2"
                >
                  <i className="fa-sharp fa-solid fa-pen"></i>
                </Button>
              </Flex>
              <Divider mb={4} />
              <div className="p-info grid grid-cols-1 gap-2 place-items-center sm:place-items-start sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {/* Gender */}
                <div className="w-full">
                  {basicDetails?.basic_details?.gender && (
                    <Tooltip label="Gender">
                      <h6 className="text-gray-700 font-normal capitalize">
                        <i
                          className={`fa-solid ${basicDetails.basic_details.gender === "Male"
                            ? "fa-mars"
                            : basicDetails.basic_details.gender === "Female"
                              ? "fa-venus"
                              : "fa-genderless"
                            } mr-2`}
                        ></i>
                        {basicDetails.basic_details.gender}
                      </h6>
                    </Tooltip>
                  )}
                </div>
                {/* Address */}
                <div className="w-full">
                  {basicDetails?.basic_details?.city?.name && basicDetails?.basic_details?.state?.name && (
                    <Tooltip label="Location">
                      <h6 className="text-gray-700 font-normal">
                        <i className="fa-solid fa-location-dot mr-2"></i>
                        {basicDetails.basic_details.city.name}, {basicDetails.basic_details.state.name},{" "}
                        {basicDetails.basic_details.country?.name}
                      </h6>
                    </Tooltip>
                  )}
                </div>
                {/* Mobile Number */}
                <div className="w-full">
                  {basicDetails?.phone && (
                    <Tooltip label="Phone Number">
                      <div className="text-gray-700 font-normal flex items-center">
                        <PhoneIcon className="mr-2" />
                        {basicDetails.phone}
                        {basicDetails?.phone_verify === 1 ? (
                          <CheckIcon color="green.500" className="ml-2" />
                        ) : (
                          <Button size="xs" colorScheme="blue" ml={2} onClick={handleVerifyPhone}>
                            Verify?
                          </Button>
                        )}
                      </div>
                    </Tooltip>
                  )}
                </div>
                {/* Email */}
                <div className="w-full">
                  <Tooltip label="Email Address">
                    <h6 className="text-gray-700 font-normal mb-2 flex items-center">
                      <EmailIcon className="mr-2" /> {basicDetails.email}
                      {basicDetails?.email_verify === 1 ? (
                        <CheckIcon color="green.500" className="ml-2" />
                      ) : (
                        <Button size="xs" colorScheme="blue" ml={2} onClick={handleVerifyEmail}>
                          Verify?
                        </Button>
                      )}
                    </h6>
                  </Tooltip>
                </div>
                {/* Work Experience */}
                <div className="w-full">
                  {basicDetails?.basic_details?.experience_year && (
                    <Tooltip label="Experience">
                      <div className="text-gray-700 font-normal flex items-center">
                        <FaBriefcase className="mr-2" />
                        {basicDetails.basic_details.experience_year} Years
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
              <Divider mb={4} />
              <div className="pb-3 capitalize text-zinc-500 text-sm">{basicDetails.basic_details?.profile_summery}</div>
              <Flex flexWrap={"wrap"} className="flex flex-wrap justify-center sm:justify-between gap-2" mt={4}>
                <Button leftIcon={<AddIcon />} colorScheme="blue" variant="outline" size="sm" onClick={onOpen3}>
                  {basicDetails.basic_details?.resume === null ? "Add Resume" : "Upload New Resume"}
                </Button>
                {basicDetails.basic_details?.resume && (
                  <Button
                    leftIcon={<ViewIcon />}
                    colorScheme="blue"
                    variant="solid"
                    size="sm"
                    onClick={() => openPdf(`${RESUME_VIEW}${basicDetails.basic_details.resume}`)}
                  >
                    View Resume
                  </Button>
                )}
              </Flex>
            </>
          ) : (
            <Alert status="info">
              <AlertIcon />
              No profile data available. Please complete your profile.
            </Alert>
          )}
        </CardBody>
      </Card>
      <Modal isOpen={isOpen1} onClose={onClose1} closeOnOverlayClick={false} isCentered={false}>
        <ModalOverlay />
        <ModalContent minW={"4xl"}>
          <ModalHeader>Edit Candidate Details</ModalHeader>
          <ModalCloseButton
            onClick={() => {
              reset()
              onClose1()
            }}
          />
          <div className="px-4 pb-4 flex flex-col gap-2">
            <form onSubmit={handleSubmit(handleUpdate)}>
              <div className="flex gap-3">
                <div className="flex w-1/2 flex-col mb-3">
                  <label>Date Of Birth</label>
                  <input
                    className="border rounded-lg border-gray-100 my-2 text-gray-500"
                    {...register("dob", {
                      required: "Date of birth is required",
                    })}
                    type="date"
                  />
                  {errors.dob && <p className="text-red-500 text-sm mt-1">{errors.dob.message}</p>}
                </div>
                <div className="flex w-1/2 flex-col mb-3">
                  <label>Address 1</label>
                  <input
                    className="border rounded-lg border-gray-100 my-2 text-gray-500"
                    {...register("address", {
                      required: "Address is required",
                    })}
                    type="text"
                  />
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address.message}</p>}
                </div>
                <div className="flex w-1/2 flex-col mb-3">
                  <label>Address 2</label>
                  <input
                    className="border rounded-lg border-gray-100 my-2 text-gray-500"
                    {...register("address1")}
                    type="text"
                    placeholder="Address "
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex w-1/2 flex-col mb-3">
                  <label>Country</label>
                  <select
                    className="border rounded-lg border-gray-100 pl-1 py-2 text-gray-500"
                    {...register("country_uuid", {
                      required: "Country is required",
                    })}
                    value={selectedCountryId}
                    onChange={(e) => {
                      countryChange(e)
                      setValue("country_uuid", e.target.value)
                    }}
                  >
                    {countryList &&
                      countryList.map((item) => (
                        <option key={item.uuid} value={item.uuid}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                  {errors.country_uuid && <p className="text-red-500 text-sm mt-1">{errors.country_uuid.message}</p>}
                </div>
                <div className="flex w-1/2 flex-col">
                  <label>State</label>
                  <select
                    className="border rounded-lg border-gray-100 pl-1 py-2 text-gray-500"
                    {...register("state_uuid", {
                      required: "State is required",
                    })}
                    value={selectedStateId}
                    onChange={(e) => {
                      stateChange(e)
                      setValue("state_uuid", e.target.value)
                    }}
                  >
                    {stateList &&
                      stateList.map((state) => (
                        <option key={state.uuid} value={state.uuid}>
                          {state.name}
                        </option>
                      ))}
                  </select>
                  {errors.state_uuid && <p className="text-red-500 text-sm mt-1">{errors.state_uuid.message}</p>}
                </div>
              </div>
              <div className="flex gap-3 ">
                <div className="flex w-1/2 flex-col">
                  <label>City</label>
                  <select
                    className="border rounded-lg border-gray-100 pl-1 py-2 text-gray-500"
                    {...register("city_uuid", {
                      required: "City is required",
                    })}
                  >
                    {Array.isArray(citiesList) &&
                      citiesList.length > 0 &&
                      citiesList.map((cities) => (
                        <option key={cities.id} value={cities.uuid}>
                          {cities?.name}
                        </option>
                      ))}
                  </select>
                  {errors.city_uuid && <p className="text-red-500 text-sm mt-1">{errors.city_uuid.message}</p>}
                </div>
                <div className="flex w-1/2 flex-col mb-3">
                  <label>Pincode</label>
                  <input
                    className="border rounded-lg border-gray-100 pl-1 py-2 text-gray-500"
                    {...register("pincode", {
                      required: "Pincode is required",
                      pattern: {
                        value: /^\d{6}$/,
                        message: "Pincode must be exactly 6 digits",
                      },
                      maxLength: {
                        value: 6,
                        message: "Pincode cannot be more than 6 digits",
                      },
                      minLength: {
                        value: 6,
                        message: "Pincode cannot be less than 6 digits",
                      },
                    })}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                  />
                  {errors.pincode && <p className="text-red-500 text-sm mt-1">{errors.pincode.message}</p>}
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex w-1/2 flex-col mb-3">
                  <label>Currently looking for a job?</label>
                  <select
                    className="border rounded-lg border-gray-100 pl-1 py-2 text-gray-500"
                    {...register("active_job_search", {
                      required: "This field is required",
                    })}
                    // Convert boolean or number to string
                    value={
                      watch("active_job_search") === true || watch("active_job_search") === 1
                        ? "true"
                        : watch("active_job_search") === false || watch("active_job_search") === 0
                          ? "false"
                          : ""
                    }
                    onChange={(e) => setValue("active_job_search", e.target.value === "true")}
                  >
                    <option value="">Select Yes / No</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                  {errors.active_job_search && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.active_job_search.message}
                    </p>
                  )}
                </div>


                <div className="flex w-1/2 flex-col mb-3">
                  <label>Currently working</label>
                  <select
                    className="border rounded-lg border-gray-100 pl-1 py-2 text-gray-500"
                    {...register("work_status", {
                      required: "This field is required",
                    })}
                    type="text"
                  >
                    <option value="">Select Yes / No</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.work_status && <p className="text-red-500 text-sm mt-1">{errors.work_status.message}</p>}
                </div>
              </div>
              <div>
                <div className="flex flex-col mb-3">
                  <label>About you (1000 Characters)</label>
                  <textarea
                    className="border rounded-lg border-gray-100 my-2 text-gray-500"
                    {...register("profile_summery", {
                      required: "This field is required",
                    })}
                    type="text"
                  ></textarea>
                  {errors.profile_summery && (
                    <p className="text-red-500 text-sm mt-1">{errors.profile_summery.message}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-3"></div>
              <div className="flex gap-3">
                <div className="flex w-1/2 flex-col mb-3">
                  <label>Experience (in Years)</label>
                  <YearPicker
                    value={watch("experience_year")}
                    onChange={(value) => setValue("experience_year", value)}
                    onBlur={() => trigger("experience_year")}
                  />
                  {errors.experience_year && (
                    <span className="text-red-500 text-sm">{errors.experience_year.message}</span>
                  )}
                </div>
                <div className="flex w-1/2 flex-col mb-3">
                  <label>Experience (in Months)</label>
                  <MonthPicker
                    value={watch("experience_month")}
                    onChange={(value) => setValue("experience_month", value)}
                    onBlur={() => trigger("experience_month")}
                  />
                  {errors.experience_month && (
                    <span className="text-red-500 text-sm">{errors.experience_month.message}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex w-1/2 flex-col mb-3">
                  <label>Marital Status</label>
                  <select
                    className="border rounded-lg border-gray-100 my-2 text-gray-500"
                    {...register("marital_status", {
                      required: "This field is required",
                    })}
                    type="text"
                  >
                    <option value="">Select your marital status</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                  {errors.marital_status && (
                    <p className="text-red-500 text-sm mt-1">{errors.marital_status.message}</p>
                  )}
                </div>
                <div className="flex w-1/2 flex-col mb-3">
                  <label>Gender</label>
                  <select
                    className="border rounded-lg border-gray-100 my-2 text-gray-500"
                    {...register("gender", {
                      required: "This field is required",
                    })}
                    type="text"
                  >
                    <option value="">Select your gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Transgender">Transgender</option>
                  </select>
                  {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender.message}</p>}
                </div>
              </div>
              <div className="w-full flex justify-center gap-2 mt-5">
                <Button colorScheme="blue" className="w-64" type="submit">
                  Update
                </Button>
                <Button
                  colorScheme="blue"
                  textColor={"blue"}
                  variant={"outline"}
                  className="w-64"
                  onClick={() => {
                    reset()
                    onClose1()
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </ModalContent>
      </Modal>

      {/* ============ MODIFIED MODAL START ============ */}
      <Modal isOpen={isOpen3} onClose={onClose3}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upload Resume</ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel htmlFor="resume">Select Resume (PDF, DOC, DOCX)</FormLabel>
              <Input
                type="file"
                id="resume"
                accept=".pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleResumeFileChange}
              />
              {previewResumeUrl && selectedResumeFile && (
                <Box mt={4}>
                  <Text>Selected File:</Text>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    {selectedResumeFile.name}
                  </Text>
                  <a
                    href={previewResumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline hover:text-blue-700"
                  >
                    Preview / Download File
                  </a>
                </Box>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose3}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={saveResume} ml={3}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* ============ MODIFIED MODAL END ============ */}

      <>
        <Modal isOpen={isOpen2} onClose={onClose2}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Upload CandidateProfile </ModalHeader>
            <ModalBody>
              <FormControl>
                <FormLabel htmlFor="logo">Select Logo</FormLabel>
                <Input type="file" id="logo" accept="image/*" onChange={handleProfileFileChange} />
                {previewProfileUrl && (
                  <Image
                    src={previewProfileUrl || "/placeholder.svg"}
                    alt="Logo Preview"
                    boxSize="200px"
                    objectFit="cover"
                    mt={4}
                  />
                )}
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={onClose2}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={saveProfile} ml={3}>
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    </div>
  )
}

export default CandidateProfile